import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { InvoiceCreateRequest, InvoiceDto, InvoiceItemWriteDto, InvoiceService, InvoiceUpdateRequest } from '../services/invoice.service';
import { ProductDto, ProductService } from '../services/product.service';

@Component({
  selector: 'app-utilities-invoices-page',
  standalone: true,
  imports: [CommonModule, FormsModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">🧾</div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Utilities > Invoices</h1>
          <p class="page-sub">Manage SSOT invoices for the organization system.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="card">
            <div class="card-hd card-hd--toggle">
              <button class="card-toggle-btn" type="button" (click)="toggleCreateInvoiceExpanded()">
                <span class="card-title">Create Invoice</span>
                <span class="card-toggle-arrow" [class.card-toggle-arrow--expanded]="isCreateInvoiceExpanded">▸</span>
              </button>
            </div>

            @if (isCreateInvoiceExpanded) {
            <div class="form">
              <label class="field">
                <span>Organization (Create only)</span>
                <select [(ngModel)]="organizationId" name="organizationId" (ngModelChange)="onOrganizationChanged()" [disabled]="isLoadingOrganizations || isCreating">
                  <option [ngValue]="0">{{ isLoadingOrganizations ? 'Loading organizations...' : 'Select organization' }}</option>
                  @for (organization of organizations; track organization.id) {
                    <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                  }
                </select>
              </label>

              <div class="field field--full section-placeholders">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                  <span class="section-placeholders-title">New Invoice</span>
                  <div style="display:flex;gap:.45rem;align-items:center">
                    <button class="btn btn--primary" [disabled]="isCreating" type="button" (click)="createInvoice()">{{ isCreating ? 'Creating...' : 'Create' }}</button>
                    <button class="btn" [disabled]="isLoadingList" type="button" (click)="clearAndRefreshList()">{{ isLoadingList ? 'Clearing...' : 'Clear' }}</button>
                  </div>
                </div>
                <div class="section-placeholders-stack">
                  <div class="section-placeholder-row">
                    <div class="invoice-header">
                      <div class="invoice-header-left">
                        <div class="business-name">United Team Building, LLC</div>
                      </div>
                      <div class="invoice-header-right">
                        <div class="invoice-header-top-row">
                          <label class="field invoice-header-field invoice-header-field--type">
                            <span>Type</span>
                            <select [(ngModel)]="createForm.type" name="createHeaderType">
                              @for (invoiceType of allowedInvoiceTypes; track invoiceType) {
                                <option [ngValue]="invoiceType">{{ invoiceType }}</option>
                              }
                            </select>
                          </label>
                          <div class="field invoice-header-field">
                            <span>Currency</span>
                            <div class="status-pill">{{ createForm.currency || 'USD' }}</div>
                          </div>
                        </div>
                        <label class="field invoice-header-field">
                          <span>Invoice Number</span>
                          <input [(ngModel)]="createForm.invoiceNumber" type="text" />
                        </label>
                        <div class="field invoice-header-field">
                          <span>Status</span>
                          <div class="status-pill">{{ createForm.status }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="section-placeholder-row section-placeholder-row--two-col">
                    <div class="section-placeholder-col invoice-info-col invoice-info-col--left">
                      <div class="invoice-info-header">Invoice Details</div>
                      <label class="field invoice-header-field">
                        <span>Issue Date</span>
                        <input [(ngModel)]="createForm.dateIssued" type="text" placeholder="2026-05-20T00:00:00Z" />
                      </label>
                      <div class="field invoice-header-field">
                        <span>Payment Terms</span>
                        <div class="status-pill">DUE UPON RECIEPT</div>
                      </div>
                      <div class="field invoice-header-field">
                        <span>Payment Method</span>
                        <div class="status-pill">Credit Card or ACH Bank Payment</div>
                      </div>
                    </div>
                    <div class="section-placeholder-col invoice-info-col invoice-info-col--right">
                      <div class="invoice-info-header">
                        Customer
                        <span [class.invoice-info-header-meta--missing]="isHelcimCustomerIdMissing">({{ helcimCustomerIdDisplay }})</span>
                      </div>
                      <div class="invoice-customer-grid">
                        <div class="field invoice-header-field">
                          <span>Contact Name</span>
                          <div class="status-pill">{{ selectedOrganization?.contactName || 'N/A' }}</div>
                        </div>
                        <div class="field invoice-header-field">
                          <span>Legal Name</span>
                          <div class="status-pill">{{ selectedOrganization?.legalName || 'N/A' }}</div>
                        </div>
                        <div class="field invoice-header-field">
                          <span>Contact Email</span>
                          <div class="status-pill">{{ selectedOrganization?.contactEmail || 'N/A' }}</div>
                        </div>
                      </div>

                      <div class="invoice-address-columns">
                        <div class="invoice-address-col">
                          <div class="invoice-address-header">Billing Information</div>
                          <div class="status-pill">{{ selectedOrganization?.billingBusinessName || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.billingContactName || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.billingContactEmail || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.billingContactPhone || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.billingAddress1 || 'N/A' }}</div>
                          @if (selectedOrganization?.billingAddress2) {
                            <div class="status-pill">{{ selectedOrganization?.billingAddress2 }}</div>
                          }
                          <div class="status-pill">{{ selectedOrganization?.billingCity || 'N/A' }}, {{ selectedOrganization?.billingState || 'N/A' }} {{ selectedOrganization?.billingZipCode || '' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.billingCountry || 'N/A' }}</div>
                        </div>
                        <div class="invoice-address-col">
                          <div class="invoice-address-header">Shipping Information</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingBusinessName || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingContactName || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingContactEmail || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingContactPhone || 'N/A' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingAddress1 || 'N/A' }}</div>
                          @if (selectedOrganization?.shippingAddress2) {
                            <div class="status-pill">{{ selectedOrganization?.shippingAddress2 }}</div>
                          }
                          <div class="status-pill">{{ selectedOrganization?.shippingCity || 'N/A' }}, {{ selectedOrganization?.shippingState || 'N/A' }} {{ selectedOrganization?.shippingZipCode || '' }}</div>
                          <div class="status-pill">{{ selectedOrganization?.shippingCountry || 'N/A' }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="section-placeholder-row section-placeholder-row--line-items">
                    <div class="line-items-section">
                      <div class="line-items-header-row">
                        <div class="line-items-col-item">
                          <div class="line-items-col-item-header">
                            <span>Item</span>
                            <button class="line-items-add-btn" type="button" aria-label="Open Product List" [disabled]="isLoadingProducts" (click)="openCreateProductListPopup()">+</button>
                          </div>
                        </div>
                        <div class="line-items-col-qty">Qty</div>
                        <div class="line-items-col-price">Price</div>
                        <div class="line-items-col-total">Total</div>
                        <div class="line-items-col-remove"></div>
                      </div>
                      @for (item of createItems; track item.productId + '-' + $index; let i = $index) {
                        <div class="line-items-data-row">
                          <div class="line-items-col-item">
                            <div class="line-item-name">{{ getCreateItemProductName(item.productId) }}</div>
                            <div class="line-item-meta">{{ item.soldDescription || 'No description' }}</div>
                            <div class="line-item-meta">SKU: {{ getCreateItemProductSku(item.productId) }}</div>
                          </div>
                          <div class="line-items-col-qty">
                            <input
                              class="line-items-qty-input"
                              type="number"
                              min="1"
                              step="1"
                              [ngModel]="item.quantity"
                              (ngModelChange)="updateCreateItemQuantity(i, $event)" />
                            <div style="display:flex;align-items:flex-start;justify-content:flex-start;gap:.65rem;flex-wrap:nowrap;margin-top:.45rem;white-space:nowrap">
                              <div
                                role="button"
                                tabindex="0"
                                style="display:grid;gap:.1rem;line-height:1.1;border:1px solid var(--color-border);border-radius:.45rem;padding:.35rem .45rem;cursor:pointer;background:transparent"
                                (click)="openCreateTaxPopup(i)"
                                (keydown.enter)="openCreateTaxPopup(i)"
                                (keydown.space)="openCreateTaxPopup(i)">
                                <span class="line-item-meta">Tax Amount</span>
                                <span class="line-item-meta">&#36;{{ item.taxAmount ?? 0 | number:'1.2-2' }}</span>
                              </div>
                              <div
                                role="button"
                                tabindex="0"
                                style="display:grid;gap:.1rem;line-height:1.1;border:1px solid var(--color-border);border-radius:.45rem;padding:.35rem .45rem;cursor:pointer;background:transparent"
                                (click)="openCreateDiscountPopup(i)"
                                (keydown.enter)="openCreateDiscountPopup(i)"
                                (keydown.space)="openCreateDiscountPopup(i)">
                                <span class="line-item-meta">Discount Amount</span>
                                <span class="line-item-meta">&#36;{{ item.discountAmount | number:'1.2-2' }}</span>
                              </div>
                            </div>
                          </div>
                          <div class="line-items-col-price">&#36;{{ item.soldPrice | number:'1.2-2' }}</div>
                          <div class="line-items-col-total">&#36;{{ getCreateItemLineTotal(item) | number:'1.2-2' }}</div>
                          <div class="line-items-col-remove">
                            <button class="btn btn--small btn--icon" type="button" aria-label="Remove line item" (click)="removeCreateItem(i)">🗑️</button>
                          </div>
                        </div>
                      } @empty {
                        <div class="line-items-empty">No line items yet. Add from Product List.</div>
                      }
                    </div>
                  </div>
                  <div class="section-placeholder-row">
                    <div class="statement-section">
                      <div class="statement-col">
                        <div class="invoice-info-header">Comments</div>
                        <label class="field invoice-header-field">
                          <span>Notes</span>
                          <textarea rows="6" [(ngModel)]="createForm.notes"></textarea>
                        </label>
                        <label class="field invoice-header-field">
                          <span>Pickup Date</span>
                          <input [(ngModel)]="createForm.pickupDate" type="date" />
                        </label>
                        <label class="field invoice-header-field">
                          <span>Pickup Name</span>
                          <input [(ngModel)]="createForm.pickupName" type="text" />
                        </label>
                        <label class="field invoice-header-field">
                          <span>Tip (Optional, Or > 0)</span>
                          <input [(ngModel)]="createForm.tipAmount" type="number" step="0.01" />
                        </label>
                        @if (isTipAmountInvalid(createForm.tipAmount)) {
                          <div class="field field--full">Tip must be greater than 0 when provided.</div>
                        }
                      </div>
                      <div class="statement-col">
                        <div class="invoice-info-header">Totals</div>
                        <div class="statement-total-line">
                          <span>Subtotal</span>
                          <strong>&#36;{{ getCreateSubtotal() | number:'1.2-2' }}</strong>
                        </div>
                        <div class="statement-total-line">
                          <span>Discount</span>
                          <strong>&#36;{{ createForm.totalDiscountAmount | number:'1.2-2' }}</strong>
                        </div>
                        <label class="field invoice-header-field">
                          <span>Discount Details</span>
                          <textarea rows="2" [(ngModel)]="createForm.totalDiscountDetails"></textarea>
                        </label>
                        <div class="statement-detail-line">
                          <span>Shipping</span>
                          <div style="display:flex;align-items:center;gap:.3rem">
                            <span>$</span>
                            <input type="number" step="0.01" min="0" placeholder="0.00" [(ngModel)]="createForm.shippingAmount" (ngModelChange)="refreshCreateAmountFromItems()" />
                          </div>
                        </div>
                        <textarea rows="2" [(ngModel)]="createForm.shippingDetails" placeholder="Shipping details"></textarea>
                        <div class="statement-detail-line">
                          <span>Tax</span>
                          <div style="display:flex;align-items:center;gap:.3rem">
                            <span>$</span>
                            <input type="number" step="0.01" min="0" placeholder="0.00" [(ngModel)]="createForm.totalTaxAmount" (ngModelChange)="refreshCreateAmountFromItems()" />
                          </div>
                        </div>
                        <textarea rows="2" [(ngModel)]="createForm.totalTaxDetails" placeholder="Tax details"></textarea>
                        <div class="statement-detail-line">
                          <span>Convenience Fee</span>
                          <div style="display:flex;align-items:center;gap:.3rem">
                            <span>$</span>
                            <input type="number" step="0.01" min="0" placeholder="0.00" [(ngModel)]="createForm.convenienceFee" (ngModelChange)="refreshCreateAmountFromItems()" />
                          </div>
                        </div>
                        <div class="statement-divider"></div>
                        <div class="statement-total-line statement-total-line--grand">
                          <span>Total</span>
                          <strong>&#36;{{ createForm.amount | number:'1.2-2' }}</strong>
                        </div>
                        <label class="statement-checkbox">
                          <input type="checkbox" [checked]="createForm.convenienceFeeEnabled" disabled />
                          <span>Fee Saver</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="section-placeholder-row">
                    <div class="invoice-footer">
                      <div class="invoice-footer-line">Thank you for your business!</div>
                      <div class="invoice-footer-line"></div>
                      <div class="invoice-footer-line">United Team Building, LLC,</div>
                      <div class="invoice-footer-line">5483 Angelonia Ter, Land O Lakes, 34639, FL, USA</div>
                      <div class="invoice-footer-line">Phone: 8134779072</div>
                      <div class="invoice-footer-line">billing@hminetworks.com</div>
                      <div class="invoice-footer-line"></div>
                      <a class="invoice-footer-link" href="https://www.UnitedTeamBuilding.com" target="_blank" rel="noopener noreferrer">https://www.UnitedTeamBuilding.com</a>
                    </div>
                  </div>
                </div>
              </div>

              @if (isCreateTaxPopupOpen) {
                <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                  <div style="width:min(440px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                    <div style="font-weight:700">Enter Tax Amount</div>
                    <label class="field">
                      <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                        <span>{{ createTaxPopupMode === 'percent' ? 'Tax Percentage' : 'Tax Amount' }}</span>
                        <div class="toggle-switch" role="group" aria-label="Create popup tax mode">
                          <button
                            class="toggle-btn"
                            type="button"
                            [style.fontSize.rem]="0.9"
                            [style.padding]="'.45rem .65rem'"
                            [style.backgroundColor]="createTaxPopupMode === 'amount' ? '#16a34a' : 'transparent'"
                            [class.toggle-btn--active]="createTaxPopupMode === 'amount'"
                            (click)="createTaxPopupMode = 'amount'">$</button>
                          <button
                            class="toggle-btn"
                            type="button"
                            [style.fontSize.rem]="0.9"
                            [style.padding]="'.45rem .65rem'"
                            [style.backgroundColor]="createTaxPopupMode === 'percent' ? '#16a34a' : 'transparent'"
                            [class.toggle-btn--active]="createTaxPopupMode === 'percent'"
                            (click)="createTaxPopupMode = 'percent'">%</button>
                        </div>
                      </div>
                      <input type="number" min="0" step="0.01" [(ngModel)]="createTaxPopupValue" />
                    </label>
                    <div style="display:flex;justify-content:flex-end;gap:.45rem">
                      <button class="btn" type="button" (click)="closeCreateTaxPopup()">Cancel</button>
                      <button class="btn btn--primary" type="button" (click)="applyCreateTaxPopup()">OK</button>
                    </div>
                  </div>
                </div>
              }

              @if (isProductListPopupOpen) {
                <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                  <div style="width:min(620px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                      <div style="font-weight:700">Product List</div>
                      <button class="btn" type="button" (click)="closeProductListPopup()">Close</button>
                    </div>
                    <div class="sub-section" style="padding:0;max-height:380px;overflow:auto">
                      <table class="result-grid">
                        <thead>
                          <tr>
                            <th>Product Name</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (product of products; track product.id) {
                            <tr>
                              <td>{{ product.name }}</td>
                              <td><button class="btn btn--small" type="button" (click)="addProductFromProductListPopup(product)">Add</button></td>
                            </tr>
                          } @empty {
                            <tr><td colspan="2" class="empty-row">No products found.</td></tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              }

              @if (isCreateDiscountPopupOpen) {
                <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                  <div style="width:min(460px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                    <div style="font-weight:700">Enter Discount Amount</div>
                    <label class="field">
                      <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                        <span>Enter Discount</span>
                        <div class="toggle-switch" role="group" aria-label="Create popup discount mode">
                          <button
                            class="toggle-btn"
                            type="button"
                            [style.fontSize.rem]="0.9"
                            [style.padding]="'.45rem .65rem'"
                            [style.backgroundColor]="createDiscountPopupMode === 'amount' ? '#16a34a' : 'transparent'"
                            [class.toggle-btn--active]="createDiscountPopupMode === 'amount'"
                            (click)="createDiscountPopupMode = 'amount'">$</button>
                          <button
                            class="toggle-btn"
                            type="button"
                            [style.fontSize.rem]="0.9"
                            [style.padding]="'.45rem .65rem'"
                            [style.backgroundColor]="createDiscountPopupMode === 'percent' ? '#16a34a' : 'transparent'"
                            [class.toggle-btn--active]="createDiscountPopupMode === 'percent'"
                            (click)="createDiscountPopupMode = 'percent'">%</button>
                        </div>
                      </div>
                      <input type="number" min="0" step="0.01" [(ngModel)]="createDiscountPopupValue" />
                    </label>
                    <div style="display:flex;justify-content:flex-end;gap:.45rem">
                      <button class="btn" type="button" (click)="closeCreateDiscountPopup()">Cancel</button>
                      <button class="btn btn--primary" type="button" (click)="applyCreateDiscountPopup()">OK</button>
                    </div>
                  </div>
                </div>
              }

              <div class="actions">
                <button class="btn btn--primary" [disabled]="isCreating" (click)="createInvoice()">{{ isCreating ? 'Creating...' : 'Create Invoice' }}</button>
                <button class="btn" [disabled]="isLoadingList" (click)="clearAndRefreshList()">{{ isLoadingList ? 'Clearing...' : 'Clear' }}</button>
              </div>
            </div>
            }
          </div>

          @if (errorMessage) {
            <div class="result result--error">{{ errorMessage }}</div>
          }

          @if (successMessage) {
            <div class="result result--ok">{{ successMessage }}</div>
          }

          <div class="card">
            <div class="card-hd"><span class="card-title">Invoices</span></div>

            <div class="list-wrap">
              <table class="result-grid">
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Date Issued</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Amount Paid</th>
                    <th>Amount Due</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (invoice of invoices; track invoice.id) {
                    <tr>
                      <td>{{ invoice.invoiceNumber }}</td>
                      <td>{{ invoice.dateIssued }}</td>
                      <td>{{ invoice.status || 'N/A' }}</td>
                      <td>{{ invoice.amount }}</td>
                      <td>{{ invoice.amountPaid }}</td>
                      <td>{{ invoice.amountDue }}</td>
                      <td><button class="btn btn--small" (click)="toggleExpanded(invoice.id)">{{ expandedInvoiceId === invoice.id ? 'Close' : 'Details' }}</button></td>
                    </tr>
                    @if (expandedInvoiceId === invoice.id) {
                      <tr>
                        <td colspan="7">
                          <div class="accordion">
                            <div class="field field--full section-placeholders">
                              <div class="section-placeholders-stack">
                                <div class="section-placeholder-row">
                                  <div class="invoice-header">
                                    <div class="invoice-header-left">
                                      <div class="business-name">United Team Building, LLC</div>
                                    </div>
                                    <div class="invoice-header-right">
                                      <div class="invoice-header-top-row">
                                        <label class="field invoice-header-field invoice-header-field--type">
                                          <span>Type</span>
                                          <select [(ngModel)]="editForm.type" name="editHeaderType">
                                            @for (invoiceType of allowedInvoiceTypes; track invoiceType) {
                                              <option [ngValue]="invoiceType">{{ invoiceType }}</option>
                                            }
                                          </select>
                                        </label>
                                        <div class="field invoice-header-field">
                                          <span>Currency</span>
                                          <div class="status-pill">{{ editForm.currency || 'USD' }}</div>
                                        </div>
                                      </div>
                                      <label class="field invoice-header-field">
                                        <span style="display:inline-flex;align-items:center;gap:.35rem">
                                          Invoice Number
                                          <span [class.invoice-info-header-meta--missing]="!editForm.helcimInvoiceId">({{ editForm.helcimInvoiceId || 'Helcim Invoice Id' }})</span>
                                        </span>
                                        <input [(ngModel)]="editForm.invoiceNumber" type="text" />
                                      </label>
                                      <label class="field invoice-header-field">
                                        <span>Status</span>
                                        <select [(ngModel)]="editForm.status" name="editStatus">
                                          @for (status of allowedStatuses; track status) {
                                            <option [ngValue]="status">{{ status }}</option>
                                          }
                                        </select>
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                <div class="section-placeholder-row section-placeholder-row--two-col">
                                  <div class="section-placeholder-col invoice-info-col invoice-info-col--left">
                                    <div class="invoice-info-header">Invoice Details</div>
                                    <label class="field invoice-header-field">
                                      <span>Issue Date</span>
                                      <input [(ngModel)]="editForm.dateIssued" type="text" readonly />
                                    </label>
                                    <label class="field invoice-header-field">
                                      <span>Change Date</span>
                                      <input [(ngModel)]="editForm.dateUpdated" type="text" readonly />
                                    </label>
                                    <label class="field invoice-header-field">
                                      <span>Paid Date</span>
                                      <input [(ngModel)]="editForm.datePaid" type="text" readonly />
                                    </label>
                                    <div class="field invoice-header-field">
                                      <span>Payment Terms</span>
                                      <div class="status-pill">DUE UPON RECIEPT</div>
                                    </div>
                                    <div class="field invoice-header-field">
                                      <span>Payment Method</span>
                                      <div class="status-pill">Credit Card or ACH Bank Payment</div>
                                    </div>
                                  </div>
                                  <div class="section-placeholder-col invoice-info-col invoice-info-col--right">
                                    <div class="invoice-info-header">
                                      Customer
                                      <span [class.invoice-info-header-meta--missing]="!expandedInvoiceOrganizationHelcimCustomerId">({{ expandedInvoiceOrganizationHelcimCustomerId || 'Helcim Customer Id' }})</span>
                                    </div>
                                    <div class="invoice-customer-grid">
                                      <div class="field invoice-header-field">
                                        <span>Contact Name</span>
                                        <div class="status-pill">{{ editForm.contactName || 'N/A' }}</div>
                                      </div>
                                      <div class="field invoice-header-field">
                                        <span>Legal Name</span>
                                        <div class="status-pill">{{ editForm.legalName || 'N/A' }}</div>
                                      </div>
                                      <div class="field invoice-header-field">
                                        <span>Contact Email</span>
                                        <div class="status-pill">{{ editForm.billingContactEmail || 'N/A' }}</div>
                                      </div>
                                    </div>

                                    <div class="invoice-address-columns">
                                      <div class="invoice-address-col">
                                        <div class="invoice-address-header">Billing Information</div>
                                        <div class="status-pill">{{ editForm.billingBusinessName || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.billingContactName || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.billingContactEmail || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.billingContactPhone || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.billingAddress1 || 'N/A' }}</div>
                                        @if (editForm.billingAddress2) {
                                          <div class="status-pill">{{ editForm.billingAddress2 }}</div>
                                        }
                                        <div class="status-pill">{{ editForm.billingCity || 'N/A' }}, {{ editForm.billingState || 'N/A' }} {{ editForm.billingZipCode || '' }}</div>
                                        <div class="status-pill">{{ editForm.billingCountry || 'N/A' }}</div>
                                      </div>
                                      <div class="invoice-address-col">
                                        <div class="invoice-address-header">Shipping Information</div>
                                        <div class="status-pill">{{ editForm.shippingBusinessName || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.shippingContactName || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.shippingContactEmail || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.shippingContactPhone || 'N/A' }}</div>
                                        <div class="status-pill">{{ editForm.shippingAddress1 || 'N/A' }}</div>
                                        @if (editForm.shippingAddress2) {
                                          <div class="status-pill">{{ editForm.shippingAddress2 }}</div>
                                        }
                                        <div class="status-pill">{{ editForm.shippingCity || 'N/A' }}, {{ editForm.shippingState || 'N/A' }} {{ editForm.shippingZipCode || '' }}</div>
                                        <div class="status-pill">{{ editForm.shippingCountry || 'N/A' }}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div class="section-placeholder-row section-placeholder-row--line-items">
                                  <div class="line-items-section">
                                    <div class="line-items-header-row">
                                      <div class="line-items-col-item">
                                        <div class="line-items-col-item-header">
                                          <span>Item</span>
                                          <button class="line-items-add-btn" type="button" aria-label="Open Product List" [disabled]="isLoadingProducts" (click)="openEditProductListPopup()">+</button>
                                        </div>
                                      </div>
                                      <div class="line-items-col-qty">Qty</div>
                                      <div class="line-items-col-price">Price</div>
                                      <div class="line-items-col-total">Total</div>
                                      <div class="line-items-col-remove"></div>
                                    </div>
                                    @for (item of editItems; track item.productId + '-' + $index; let i = $index) {
                                      <div class="line-items-data-row">
                                        <div class="line-items-col-item">
                                          <div class="line-item-name">{{ getEditItemProductName(item.productId) }}</div>
                                          <div class="line-item-meta">{{ item.soldDescription || 'No description' }}</div>
                                          <div class="line-item-meta">SKU: {{ getEditItemProductSku(item.productId) }}</div>
                                        </div>
                                        <div class="line-items-col-qty">
                                          <input
                                            class="line-items-qty-input"
                                            type="number"
                                            min="1"
                                            step="1"
                                            [ngModel]="item.quantity"
                                            (ngModelChange)="updateEditItemQuantity(i, $event)" />
                                          <div style="display:flex;align-items:flex-start;justify-content:flex-start;gap:.65rem;flex-wrap:nowrap;margin-top:.45rem;white-space:nowrap">
                                            <div
                                              role="button"
                                              tabindex="0"
                                              style="display:grid;gap:.1rem;line-height:1.1;border:1px solid var(--color-border);border-radius:.45rem;padding:.35rem .45rem;cursor:pointer;background:transparent"
                                              (click)="openEditTaxPopup(i)"
                                              (keydown.enter)="openEditTaxPopup(i)"
                                              (keydown.space)="openEditTaxPopup(i)">
                                              <span class="line-item-meta">Tax Amount</span>
                                              <span class="line-item-meta">&#36;{{ item.taxAmount ?? 0 | number:'1.2-2' }}</span>
                                            </div>
                                            <div
                                              role="button"
                                              tabindex="0"
                                              style="display:grid;gap:.1rem;line-height:1.1;border:1px solid var(--color-border);border-radius:.45rem;padding:.35rem .45rem;cursor:pointer;background:transparent"
                                              (click)="openEditDiscountPopup(i)"
                                              (keydown.enter)="openEditDiscountPopup(i)"
                                              (keydown.space)="openEditDiscountPopup(i)">
                                              <span class="line-item-meta">Discount Amount</span>
                                              <span class="line-item-meta">&#36;{{ item.discountAmount | number:'1.2-2' }}</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div class="line-items-col-price">&#36;{{ item.soldPrice | number:'1.2-2' }}</div>
                                        <div class="line-items-col-total">&#36;{{ getEditItemLineTotal(item) | number:'1.2-2' }}</div>
                                        <div class="line-items-col-remove">
                                          <button class="btn btn--small btn--icon" type="button" aria-label="Remove line item" (click)="removeEditItem(i)">🗑️</button>
                                        </div>
                                      </div>
                                    } @empty {
                                      <div class="line-items-empty">No line items yet. Add from Product List.</div>
                                    }
                                  </div>
                                </div>

                                <div class="section-placeholder-row">
                                  <div class="statement-section">
                                    <div class="statement-col">
                                      <div class="invoice-info-header">Comments</div>
                                      <label class="field invoice-header-field">
                                        <span>Notes</span>
                                        <textarea rows="6" [(ngModel)]="editForm.notes"></textarea>
                                      </label>
                                      <label class="field invoice-header-field">
                                        <span>Pickup Date</span>
                                        <input [(ngModel)]="editForm.pickupDate" type="date" />
                                      </label>
                                      <label class="field invoice-header-field">
                                        <span>Pickup Name</span>
                                        <input [(ngModel)]="editForm.pickupName" type="text" />
                                      </label>
                                      <label class="field invoice-header-field">
                                        <span>Tip (Optional, Or > 0)</span>
                                        <input [(ngModel)]="editForm.tipAmount" type="number" step="0.01" />
                                      </label>
                                      @if (isTipAmountInvalid(editForm.tipAmount)) {
                                        <div class="field field--full">Tip must be greater than 0 when provided.</div>
                                      }
                                    </div>
                                    <div class="statement-col">
                                      <div class="invoice-info-header">Totals</div>
                                      <div class="statement-total-line">
                                        <span>Subtotal</span>
                                        <strong>&#36;{{ getEditSubtotal() | number:'1.2-2' }}</strong>
                                      </div>
                                      <div class="statement-total-line">
                                        <span>Discount</span>
                                        <strong>&#36;{{ editForm.totalDiscountAmount | number:'1.2-2' }}</strong>
                                      </div>
                                      <label class="field invoice-header-field">
                                        <span>Discount Details</span>
                                        <textarea rows="2" [(ngModel)]="editForm.totalDiscountDetails"></textarea>
                                      </label>
                                      <div class="statement-detail-line">
                                        <span>Shipping</span>
                                        <div style="display:flex;align-items:center;gap:.3rem">
                                          <span>$</span>
                                          <input type="number" step="0.01" min="0" placeholder="0.00" [(ngModel)]="editForm.shippingAmount" (ngModelChange)="refreshEditTotalsFromItems()" />
                                        </div>
                                      </div>
                                      <textarea rows="2" [(ngModel)]="editForm.shippingDetails" placeholder="Shipping details"></textarea>
                                      <div class="statement-detail-line">
                                        <span>Tax</span>
                                        <div style="display:flex;align-items:center;gap:.3rem">
                                          <span>$</span>
                                          <input type="number" step="0.01" min="0" placeholder="0.00" [(ngModel)]="editForm.totalTaxAmount" (ngModelChange)="refreshEditTotalsFromItems()" />
                                        </div>
                                      </div>
                                      <textarea rows="2" [(ngModel)]="editForm.totalTaxDetails" placeholder="Tax details"></textarea>
                                      <div class="statement-divider"></div>
                                      <div class="statement-total-line statement-total-line--grand">
                                        <span>Total</span>
                                        <strong>&#36;{{ editForm.amount | number:'1.2-2' }}</strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div class="section-placeholder-row">
                                  <div class="invoice-footer">
                                    <div class="invoice-footer-line">Thank you for your business!</div>
                                    <div class="invoice-footer-line"></div>
                                    <div class="invoice-footer-line">United Team Building, LLC,</div>
                                    <div class="invoice-footer-line">5483 Angelonia Ter, Land O Lakes, 34639, FL, USA</div>
                                    <div class="invoice-footer-line">Phone: 8134779072</div>
                                    <div class="invoice-footer-line">billing@hminetworks.com</div>
                                    <div class="invoice-footer-line"></div>
                                    <a class="invoice-footer-link" href="https://www.UnitedTeamBuilding.com" target="_blank" rel="noopener noreferrer">https://www.UnitedTeamBuilding.com</a>
                                  </div>
                                </div>
                              </div>
                            </div>

                            @if (isEditTaxPopupOpen) {
                              <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                                <div style="width:min(440px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                                  <div style="font-weight:700">Enter Tax Amount</div>
                                  <label class="field">
                                    <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                                      <span>{{ editTaxPopupMode === 'percent' ? 'Tax Percentage' : 'Tax Amount' }}</span>
                                      <div class="toggle-switch" role="group" aria-label="Edit popup tax mode">
                                        <button
                                          class="toggle-btn"
                                          type="button"
                                          [style.fontSize.rem]="0.9"
                                          [style.padding]="'.45rem .65rem'"
                                          [style.backgroundColor]="editTaxPopupMode === 'amount' ? '#16a34a' : 'transparent'"
                                          [class.toggle-btn--active]="editTaxPopupMode === 'amount'"
                                          (click)="editTaxPopupMode = 'amount'">$</button>
                                        <button
                                          class="toggle-btn"
                                          type="button"
                                          [style.fontSize.rem]="0.9"
                                          [style.padding]="'.45rem .65rem'"
                                          [style.backgroundColor]="editTaxPopupMode === 'percent' ? '#16a34a' : 'transparent'"
                                          [class.toggle-btn--active]="editTaxPopupMode === 'percent'"
                                          (click)="editTaxPopupMode = 'percent'">%</button>
                                      </div>
                                    </div>
                                    <input type="number" min="0" step="0.01" [(ngModel)]="editTaxPopupValue" />
                                  </label>
                                  <div style="display:flex;justify-content:flex-end;gap:.45rem">
                                    <button class="btn" type="button" (click)="closeEditTaxPopup()">Cancel</button>
                                    <button class="btn btn--primary" type="button" (click)="applyEditTaxPopup()">OK</button>
                                  </div>
                                </div>
                              </div>
                            }

                            @if (isEditDiscountPopupOpen) {
                              <div style="position:fixed;inset:0;background:rgba(2,6,23,.78);display:grid;place-items:center;z-index:1000">
                                <div style="width:min(460px,92vw);background:#0f172a;border:1px solid #334155;border-radius:.7rem;padding:1rem;display:grid;gap:.75rem;box-shadow:0 20px 45px rgba(2,6,23,.55)">
                                  <div style="font-weight:700">Enter Discount Amount</div>
                                  <label class="field">
                                    <div style="display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap">
                                      <span>Enter Discount</span>
                                      <div class="toggle-switch" role="group" aria-label="Edit popup discount mode">
                                        <button
                                          class="toggle-btn"
                                          type="button"
                                          [style.fontSize.rem]="0.9"
                                          [style.padding]="'.45rem .65rem'"
                                          [style.backgroundColor]="editDiscountPopupMode === 'amount' ? '#16a34a' : 'transparent'"
                                          [class.toggle-btn--active]="editDiscountPopupMode === 'amount'"
                                          (click)="editDiscountPopupMode = 'amount'">$</button>
                                        <button
                                          class="toggle-btn"
                                          type="button"
                                          [style.fontSize.rem]="0.9"
                                          [style.padding]="'.45rem .65rem'"
                                          [style.backgroundColor]="editDiscountPopupMode === 'percent' ? '#16a34a' : 'transparent'"
                                          [class.toggle-btn--active]="editDiscountPopupMode === 'percent'"
                                          (click)="editDiscountPopupMode = 'percent'">%</button>
                                      </div>
                                    </div>
                                    <input type="number" min="0" step="0.01" [(ngModel)]="editDiscountPopupValue" />
                                  </label>
                                  <div style="display:flex;justify-content:flex-end;gap:.45rem">
                                    <button class="btn" type="button" (click)="closeEditDiscountPopup()">Cancel</button>
                                    <button class="btn btn--primary" type="button" (click)="applyEditDiscountPopup()">OK</button>
                                  </div>
                                </div>
                              </div>
                            }

                            <div class="actions">
                              <button class="btn btn--primary" [disabled]="isSaving" (click)="saveInvoice(invoice.id)">{{ isSaving ? 'Saving...' : 'Save' }}</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                  } @empty {
                    <tr><td colspan="7" class="empty-row">No invoices loaded.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1100px;margin:0 auto}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);margin-bottom:1rem}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-hd--toggle{padding:0}.card-toggle-btn{width:100%;display:flex;align-items:center;justify-content:space-between;gap:.6rem;padding:.8rem 1rem;border:none;background:transparent;color:inherit;cursor:pointer}.card-title{font-weight:600}.card-toggle-arrow{display:inline-block;transition:transform .16s ease;font-size:1.45rem;color:#bfdbfe;line-height:1}.card-toggle-arrow--expanded{transform:rotate(90deg)}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.items-two-col{display:grid;grid-template-columns:1fr 1fr;gap:.8rem}.field input,.field select,.field textarea{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.section-placeholders{border:1px solid #93c5fd;border-radius:.7rem;padding:.9rem;background:rgba(147,197,253,.12);margin:.35rem 0}.section-placeholders-title{font-weight:700;color:#bfdbfe;text-transform:uppercase;letter-spacing:.03em}.section-placeholders-stack{display:grid;gap:.65rem;margin-top:.25rem}.section-placeholder-row{border:1px solid #cbd5e1;border-radius:.55rem;padding:.8rem;background:rgba(148,163,184,.18);font-weight:600;font-size:.95rem;color:#e2e8f0;width:100%}.section-placeholder-row--line-items{border-top:2px solid #bfdbfe;padding-top:1rem}.line-items-section{display:grid;gap:.55rem}.line-items-header-row,.line-items-data-row{display:grid;grid-template-columns:minmax(0,1fr) 4.5rem 6.5rem 6.5rem 3rem;align-items:start;gap:.5rem}.line-items-header-row{font-weight:700;border-bottom:1px solid #94a3b8;padding-bottom:.35rem}.line-items-data-row{border-bottom:1px dashed #94a3b8;padding:.35rem 0}.line-items-col-item{min-width:0}.line-items-col-item-header{display:flex;align-items:center;justify-content:flex-start;gap:.3rem}.line-items-add-btn{border:none;background:transparent;color:#86efac;cursor:pointer;font-size:1.2rem;font-weight:700;line-height:1;padding:0 .1rem;display:inline-flex;align-items:center;justify-content:center}.line-items-add-btn:disabled{opacity:.55;cursor:not-allowed}.line-items-col-qty,.line-items-col-price,.line-items-col-total{text-align:right}.line-items-col-remove{display:flex;justify-content:center;align-items:flex-start}.line-items-qty-input{max-width:4rem;text-align:right}.line-item-name{font-weight:700}.line-item-meta{font-size:.8rem;color:#cbd5e1}.line-items-empty{color:#cbd5e1;padding:.4rem 0}.statement-section{display:grid;grid-template-columns:1fr 1fr;gap:.8rem}.statement-col{display:grid;gap:.55rem;align-content:start}.statement-total-line{display:flex;justify-content:space-between;align-items:center;gap:.6rem}.statement-total-line--grand{font-size:1.05rem}.statement-detail-line{display:grid;grid-template-columns:1fr auto;align-items:center;gap:.6rem}.statement-detail-line input{max-width:8rem;text-align:right}.statement-divider{border-top:1px solid #94a3b8;margin:.2rem 0}.statement-checkbox{display:flex;align-items:center;gap:.45rem}.invoice-header{display:grid;gap:1rem;grid-template-columns:1.4fr 1fr;align-items:start}.business-name{font-size:1.35rem;font-weight:700;line-height:1.2;color:#e2e8f0}.invoice-header-right{display:grid;gap:.6rem}.invoice-header-top-row{display:grid;grid-template-columns:auto 1fr;gap:.6rem;align-items:start}.invoice-header-field{margin:0}.invoice-header-field--type select{max-width:170px}.status-pill{border:1px solid #93c5fd;border-radius:.45rem;background:rgba(147,197,253,.15);padding:.45rem .55rem;font-weight:600;color:#dbeafe}.invoice-footer{display:grid;gap:.2rem}.invoice-footer-line{line-height:1.35}.invoice-footer-link{color:#bfdbfe;text-decoration:underline;word-break:break-word}.section-placeholder-row--two-col{display:grid;grid-template-columns:3fr 7fr;gap:.65rem;padding:.65rem}.section-placeholder-col{border:1px dashed #94a3b8;border-radius:.45rem;padding:.7rem;background:rgba(148,163,184,.12);font-weight:500}.invoice-info-col{display:grid;gap:.6rem;align-content:start}.invoice-info-header{font-size:1rem;font-weight:700;color:#e2e8f0}.invoice-info-header-meta--missing{font-style:italic;color:#cbd5e1}.invoice-customer-grid{display:grid;gap:.55rem}.invoice-address-columns{display:grid;grid-template-columns:1fr 1fr;gap:.65rem}.invoice-address-col{display:grid;gap:.45rem}.invoice-address-header{font-size:.9rem;font-weight:700;color:#cbd5e1}.input--italic{font-style:italic}.actions{grid-column:1 / -1;display:flex;gap:.5rem;justify-content:flex-start}.btn{align-items:center;border:1px solid var(--color-border);border-radius:.55rem;background:transparent;color:var(--color-text);cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;line-height:1;padding:.55rem .85rem}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.btn--small{padding:.35rem .55rem;font-size:.72rem}.btn--icon{padding:.45rem .55rem;font-size:1rem;line-height:1}.btn:disabled{opacity:.55;cursor:not-allowed}.result{margin:0 0 1rem;padding:.75rem;border-radius:.55rem;font-size:.9rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}.list-wrap,.sub-section{padding:1rem;border:1px solid var(--color-border);border-radius:.55rem}.result-grid{border-collapse:collapse;width:100%}.result-grid th,.result-grid td{border:1px solid var(--color-border);padding:.5rem .55rem;text-align:left;vertical-align:top}.result-grid th{background:color-mix(in srgb,var(--color-surface-1) 75%,transparent)}.th-wrap{display:inline-block;white-space:normal;line-height:1.1}.th-with-toggle{align-items:center;display:flex;gap:.4rem;flex-wrap:wrap}.toggle-switch{display:inline-flex;border:1px solid var(--color-border);border-radius:.45rem;overflow:hidden}.toggle-btn{background:transparent;border:none;color:var(--color-text);cursor:pointer;font-size:.72rem;font-weight:700;line-height:1;padding:.3rem .45rem}.toggle-btn--active{background:color-mix(in srgb,#2563eb 26%,transparent)}.action-col{width:1%;white-space:nowrap}.item-description{white-space:normal;word-break:break-word}.empty-row{color:var(--color-muted);text-align:center}.accordion{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:.5rem 0}`]
})
export class UtilitiesInvoicesPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly productService = inject(ProductService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  organizations: OrganizationDto[] = [];
  organizationId = 0;
  isLoadingOrganizations = false;
  isLoadingList = false;
  isCreating = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';

  invoices: InvoiceDto[] = [];
  expandedInvoiceId: number | null = null;
  products: ProductDto[] = [];
  createItems: InvoiceItemWriteDto[] = [];
  createProductAmounts: Record<number, { taxAmount: number; discountAmount: number }> = {};
  editProductAmounts: Record<number, { taxAmount: number; discountAmount: number }> = {};
  createDiscountMode: 'amount' | 'percent' = 'amount';
  editDiscountMode: 'amount' | 'percent' = 'amount';
  isCreateTaxPopupOpen = false;
  createTaxPopupItemIndex: number | null = null;
  createTaxPopupValue = 0;
  isCreateDiscountPopupOpen = false;
  createDiscountPopupItemIndex: number | null = null;
  createDiscountPopupValue = 0;
  createDiscountPopupMode: 'amount' | 'percent' = 'amount';
  createTaxPopupMode: 'amount' | 'percent' = 'percent';
  isEditTaxPopupOpen = false;
  editTaxPopupItemIndex: number | null = null;
  editTaxPopupValue = 0;
  editTaxPopupMode: 'amount' | 'percent' = 'percent';
  isEditDiscountPopupOpen = false;
  editDiscountPopupItemIndex: number | null = null;
  editDiscountPopupValue = 0;
  editDiscountPopupMode: 'amount' | 'percent' = 'amount';
  isLoadingProducts = false;
  isProductListPopupOpen = false;
  productListPopupContext: 'create' | 'edit' = 'create';
  isCreateInvoiceExpanded = true;

  readonly allowedStatuses = ['CANCELED', 'DUE', 'PAID', 'SHIPPED', 'COMPLETED', 'REFUNDED', 'IN_PROGRESS'];
  readonly allowedInvoiceTypes = ['ESTIMATE', 'QUOTE', 'ORDER', 'PURCHASE_ORDER', 'INVOICE', 'STATEMENT', 'REGISTRATION', 'CREDIT'];

  get selectedOrganizationHelcimCustomerId(): number | null {
    return this.parseOrganizationHelcimCustomerId(this.selectedOrganization);
  }

  get expandedInvoiceOrganizationHelcimCustomerId(): number | null {
    if (this.expandedInvoiceId === null) {
      return null;
    }

    const expandedInvoice = this.invoices.find(invoice => invoice.id === this.expandedInvoiceId);
    if (!expandedInvoice) {
      return null;
    }

    const organization = this.organizations.find(item => item.id === expandedInvoice.organizationId) ?? null;
    return this.parseOrganizationHelcimCustomerId(organization);
  }

  private parseOrganizationHelcimCustomerId(organization: OrganizationDto | null): number | null {
    const rawCustomerId = organization?.helcimCustomerId;

    if (typeof rawCustomerId === 'number') {
      return Number.isFinite(rawCustomerId) && rawCustomerId > 0 ? rawCustomerId : null;
    }

    if (typeof rawCustomerId === 'string') {
      const parsedCustomerId = Number(rawCustomerId.trim());
      return Number.isFinite(parsedCustomerId) && parsedCustomerId > 0 ? parsedCustomerId : null;
    }

    return null;
  }

  createForm = {
    invoiceNumber: this.createDefaultInvoiceNumber(),
    helcimInvoiceId: '',
    currency: 'USD',
    pickupDate: '',
    type: 'INVOICE',
    status: 'DUE',
    pickupName: '',
    datePaid: '',
    totalTaxAmount: 0,
    totalTaxDetails: '',
    totalDiscountAmount: 0,
    totalDiscountDetails: '',
    dateIssued: this.getCurrentUtcIsoTimestamp(),
    amount: 0,
    amountPaid: 0,
    amountDue: 0,
    shippingAmount: 0,
    shippingDetails: '',
    tipAmount: null as number | null,
    depositAmount: 0,
    convenienceFee: 0,
    convenienceFeeEnabled: true,
    notes: ''
  };

  editForm = {
    invoiceNumber: '',
    helcimInvoiceId: '',
    currency: 'USD',
    type: 'INVOICE',
    status: 'DUE',
    pickupDate: '',
    dateIssued: '',
    dateUpdated: '',
    datePaid: '',
    amount: 0,
    amountPaid: 0,
    amountDue: 0,
    shippingAmount: 0,
    shippingDetails: '',
    totalTaxAmount: 0,
    totalTaxDetails: '',
    totalDiscountAmount: 0,
    totalDiscountDetails: '',
    tipAmount: null as number | null,
    pickupName: '',
    notes: '',
    contactName: '',
    legalName: '',
    billingBusinessName: '',
    shippingBusinessName: '',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingState: '',
    billingCountry: '',
    billingZipCode: '',
    shippingContactName: '',
    shippingContactEmail: '',
    shippingContactPhone: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingCountry: '',
    shippingZipCode: ''
  };

  editItems: InvoiceItemWriteDto[] = [];

  get selectedOrganization(): OrganizationDto | null {
    return this.organizations.find(organization => organization.id === this.organizationId) ?? null;
  }

  get isHelcimCustomerIdMissing(): boolean {
    return this.selectedOrganizationHelcimCustomerId === null;
  }

  get helcimCustomerIdDisplay(): string {
    if (this.selectedOrganizationHelcimCustomerId !== null) {
      return String(this.selectedOrganizationHelcimCustomerId);
    }

    return 'Helcim Customer Id';
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.loadOrganizationsAsync(), this.loadInvoices(), this.loadProductsAsync()]);
  }

  private async loadProductsAsync(): Promise<void> {
    this.isLoadingProducts = true;
    try {
      this.products = await firstValueFrom(this.productService.getAll());
    } catch {
      this.products = [];
    } finally {
      this.isLoadingProducts = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async clearAndRefreshList(): Promise<void> {
    this.resetCreateForm();
    await this.loadInvoices();
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      if (isSiteAdmin) {
        this.organizations = await firstValueFrom(this.organizationService.getAll());
        if (this.organizations.length === 0) {
          const myOrganization = await firstValueFrom(this.organizationService.getMyOrganization());
          this.organizations = [myOrganization];
        }
      } else {
        this.organizations = [await firstValueFrom(this.organizationService.getMyOrganization())];
      }

      if (this.organizationId <= 0 && this.organizations.length > 0) {
        this.organizationId = this.organizations[0].id;
      }

      this.onOrganizationChanged();
    } catch {
      this.organizations = [];
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async loadInvoices(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoadingList = true;

    try {
      this.invoices = await firstValueFrom(this.invoiceService.getAll());
      this.expandedInvoiceId = null;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to load invoices.';
    } finally {
      this.isLoadingList = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async createInvoice(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.organizationId <= 0 || !this.createForm.invoiceNumber.trim()) {
      this.errorMessage = 'Organization and invoice number are required.';
      return;
    }

    if (this.createItems.length === 0) {
      this.errorMessage = 'At least one item is required. Add an item from the Products section.';
      return;
    }

    const createTipAmount = this.normalizeTipAmount(this.createForm.tipAmount);

    if (this.isTipAmountInvalid(createTipAmount)) {
      this.errorMessage = 'Tip must be greater than 0 when provided.';
      return;
    }

    this.isCreating = true;

    try {
      this.createForm.dateIssued = this.getCurrentUtcIsoTimestamp();
      const depositAmount = 0;
      const request: InvoiceCreateRequest = {
        organizationId: this.organizationId,
        invoiceNumber: this.createForm.invoiceNumber.trim(),
        helcimInvoiceId: this.createForm.helcimInvoiceId.trim() || undefined,
        currency: this.createForm.currency.trim().toUpperCase(),
        pickupDate: this.createForm.pickupDate.trim() || undefined,
        type: this.createForm.type.trim(),
        status: 'DUE',
        pickupName: this.createForm.pickupName.trim(),
        datePaid: undefined,
        dateIssued: this.createForm.dateIssued.trim() || undefined,
        totalTaxAmount: this.createForm.totalTaxAmount,
        totalTaxDetails: this.createForm.totalTaxDetails.trim() || undefined,
        totalDiscountAmount: this.createForm.totalDiscountAmount,
        totalDiscountDetails: this.createForm.totalDiscountDetails.trim() || undefined,
        amount: this.createForm.amount,
        amountPaid: depositAmount,
        amountDue: this.createForm.amountDue,
        shippingAmount: this.createForm.shippingAmount,
        shippingDetails: this.createForm.shippingDetails.trim() || undefined,
        tipAmount: createTipAmount,
        depositAmount,
        convenienceFee: this.createForm.convenienceFee,
        convenienceFeeEnabled: this.createForm.convenienceFeeEnabled,
        notes: this.createForm.notes.trim() || undefined,
        items: this.createItems
      };

      await firstValueFrom(this.invoiceService.create(request));
      this.successMessage = 'Invoice created successfully.';
      await this.loadInvoices();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Create invoice failed.';
    } finally {
      this.isCreating = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  addProductToCreateItems(product: ProductDto): void {
    const quantity = 1;
    const soldPrice = product.defaultPrice ?? 0;

    this.createItems.push({
      productId: product.id,
      quantity,
      soldPrice,
      soldDescription: product.description ?? undefined,
      taxAmount: 0,
      discountAmount: 0
    });

    this.refreshCreateAmountFromItems();
  }

  openCreateProductListPopup(): void {
    this.productListPopupContext = 'create';
    this.isProductListPopupOpen = true;
  }

  openEditProductListPopup(): void {
    this.productListPopupContext = 'edit';
    this.isProductListPopupOpen = true;
  }

  closeProductListPopup(): void {
    this.isProductListPopupOpen = false;
  }

  toggleCreateInvoiceExpanded(): void {
    this.isCreateInvoiceExpanded = !this.isCreateInvoiceExpanded;
  }

  addProductFromProductListPopup(product: ProductDto): void {
    if (this.productListPopupContext === 'edit') {
      this.addProductToEditItems(product);
      return;
    }

    this.addProductToCreateItems(product);
  }

  removeCreateItem(index: number): void {
    if (index < 0 || index >= this.createItems.length) {
      return;
    }

    this.createItems.splice(index, 1);
    this.refreshCreateAmountFromItems();
  }

  openCreateTaxPopup(index: number): void {
    if (index < 0 || index >= this.createItems.length) {
      return;
    }

    const item = this.createItems[index];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const currentTaxAmount = Number.isFinite(item.taxAmount ?? 0) ? (item.taxAmount ?? 0) : 0;

    this.createTaxPopupItemIndex = index;
    this.createTaxPopupValue = lineTotal > 0
      ? this.roundMoney((currentTaxAmount / lineTotal) * 100)
      : 0;
    this.createTaxPopupMode = 'percent';
    this.isCreateTaxPopupOpen = true;
  }

  closeCreateTaxPopup(): void {
    this.isCreateTaxPopupOpen = false;
    this.createTaxPopupItemIndex = null;
    this.createTaxPopupValue = 0;
    this.createTaxPopupMode = 'percent';
  }

  applyCreateTaxPopup(): void {
    if (this.createTaxPopupItemIndex === null || this.createTaxPopupItemIndex < 0 || this.createTaxPopupItemIndex >= this.createItems.length) {
      this.closeCreateTaxPopup();
      return;
    }

    const item = this.createItems[this.createTaxPopupItemIndex];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const amountOrPercent = this.normalizeLineItemAmount(this.createTaxPopupValue);
    item.taxAmount = this.roundMoney(this.createTaxPopupMode === 'percent'
      ? lineTotal * (amountOrPercent / 100)
      : amountOrPercent);

    this.closeCreateTaxPopup();
    this.refreshCreateAmountFromItems();
  }

  openCreateDiscountPopup(index: number): void {
    if (index < 0 || index >= this.createItems.length) {
      return;
    }

    this.createDiscountPopupItemIndex = index;
    this.createDiscountPopupValue = Number.isFinite(this.createItems[index].discountAmount) ? this.createItems[index].discountAmount : 0;
    this.createDiscountPopupMode = 'amount';
    this.isCreateDiscountPopupOpen = true;
  }

  closeCreateDiscountPopup(): void {
    this.isCreateDiscountPopupOpen = false;
    this.createDiscountPopupItemIndex = null;
    this.createDiscountPopupValue = 0;
    this.createDiscountPopupMode = 'amount';
  }

  applyCreateDiscountPopup(): void {
    if (this.createDiscountPopupItemIndex === null || this.createDiscountPopupItemIndex < 0 || this.createDiscountPopupItemIndex >= this.createItems.length) {
      this.closeCreateDiscountPopup();
      return;
    }

    const item = this.createItems[this.createDiscountPopupItemIndex];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const amountOrPercent = this.normalizeLineItemAmount(this.createDiscountPopupValue);

    item.discountAmount = this.roundMoney(this.createDiscountPopupMode === 'percent'
      ? lineTotal * (amountOrPercent / 100)
      : amountOrPercent);

    this.closeCreateDiscountPopup();
    this.refreshCreateAmountFromItems();
  }

  onOrganizationChanged(): void {
    // Organization selection affects the displayed Helcim customer id via selectedOrganizationHelcimCustomerId.
  }

  refreshCreateAmountFromItems(): void {
    const lineSubtotal = this.createItems.reduce((total, item) => {
      const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
      const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
      return total + (quantity * soldPrice);
    }, 0);

    const lineTaxTotal = this.createItems.reduce((total, item) => {
      const taxAmount = Number.isFinite(item.taxAmount ?? 0) ? (item.taxAmount ?? 0) : 0;
      return total + taxAmount;
    }, 0);

    const lineDiscountTotal = this.createItems.reduce((total, item) => {
      const discountAmount = Number.isFinite(item.discountAmount) ? item.discountAmount : 0;
      return total + discountAmount;
    }, 0);

    const shippingAmount = Number.isFinite(this.createForm.shippingAmount) ? this.createForm.shippingAmount : 0;
    const convenienceFee = Number.isFinite(this.createForm.convenienceFee) ? this.createForm.convenienceFee : 0;

    this.createForm.totalTaxAmount = this.roundMoney(lineTaxTotal);
    this.createForm.totalDiscountAmount = this.roundMoney(lineDiscountTotal);
    this.createForm.amount = this.roundMoney(lineSubtotal - lineDiscountTotal + shippingAmount + lineTaxTotal + convenienceFee);
    this.createForm.amountDue = this.createForm.amount;
  }

  addProductToEditItems(product: ProductDto): void {
    const quantity = 1;
    const soldPrice = product.defaultPrice ?? 0;

    this.editItems.push({
      productId: product.id,
      quantity,
      soldPrice,
      soldDescription: product.description ?? undefined,
      taxAmount: 0,
      discountAmount: 0
    });

    this.refreshEditTotalsFromItems();
  }

  removeEditItem(index: number): void {
    if (index < 0 || index >= this.editItems.length) {
      return;
    }

    this.editItems.splice(index, 1);
    this.refreshEditTotalsFromItems();
  }

  setCreateDiscountMode(mode: 'amount' | 'percent'): void {
    this.createDiscountMode = mode;
  }

  setEditDiscountMode(mode: 'amount' | 'percent'): void {
    this.editDiscountMode = mode;
  }

  getCreateProductTaxAmount(productId: number): number {
    return this.getOrCreateAmounts(this.createProductAmounts, productId).taxAmount;
  }

  setCreateProductTaxAmount(productId: number, value: number | string): void {
    this.getOrCreateAmounts(this.createProductAmounts, productId).taxAmount = this.normalizeLineItemAmount(value);
  }

  getCreateProductDiscountAmount(productId: number): number {
    return this.getOrCreateAmounts(this.createProductAmounts, productId).discountAmount;
  }

  setCreateProductDiscountAmount(productId: number, value: number | string): void {
    this.getOrCreateAmounts(this.createProductAmounts, productId).discountAmount = this.normalizeLineItemAmount(value);
  }

  getEditProductTaxAmount(productId: number): number {
    return this.getOrCreateAmounts(this.editProductAmounts, productId).taxAmount;
  }

  setEditProductTaxAmount(productId: number, value: number | string): void {
    this.getOrCreateAmounts(this.editProductAmounts, productId).taxAmount = this.normalizeLineItemAmount(value);
  }

  getEditProductDiscountAmount(productId: number): number {
    return this.getOrCreateAmounts(this.editProductAmounts, productId).discountAmount;
  }

  setEditProductDiscountAmount(productId: number, value: number | string): void {
    this.getOrCreateAmounts(this.editProductAmounts, productId).discountAmount = this.normalizeLineItemAmount(value);
  }

  getCreateItemProductName(productId: number): string {
    return this.products.find(product => product.id === productId)?.name ?? `Product ${productId}`;
  }

  getCreateItemProductSku(productId: number): string {
    return this.products.find(product => product.id === productId)?.sku ?? 'N/A';
  }

  getEditItemProductName(productId: number): string {
    return this.products.find(product => product.id === productId)?.name ?? `Product ${productId}`;
  }

  getEditItemProductSku(productId: number): string {
    return this.products.find(product => product.id === productId)?.sku ?? 'N/A';
  }

  getCreateItemLineTotal(item: InvoiceItemWriteDto): number {
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    return this.roundMoney(quantity * soldPrice);
  }

  getEditItemLineTotal(item: InvoiceItemWriteDto): number {
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    return this.roundMoney(quantity * soldPrice);
  }

  getCreateSubtotal(): number {
    return this.roundMoney(this.createItems.reduce((total, item) => {
      const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
      const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
      return total + (quantity * soldPrice);
    }, 0));
  }

  getEditSubtotal(): number {
    return this.roundMoney(this.editItems.reduce((total, item) => {
      const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
      const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
      return total + (quantity * soldPrice);
    }, 0));
  }

  updateCreateItemQuantity(index: number, value: number | string): void {
    if (index < 0 || index >= this.createItems.length) {
      return;
    }

    const parsed = Number(value);
    const normalized = Number.isFinite(parsed) ? Math.floor(parsed) : 1;
    this.createItems[index].quantity = normalized >= 1 ? normalized : 1;
    this.refreshCreateAmountFromItems();
  }

  updateEditItemQuantity(index: number, value: number | string): void {
    if (index < 0 || index >= this.editItems.length) {
      return;
    }

    const parsed = Number(value);
    const normalized = Number.isFinite(parsed) ? Math.floor(parsed) : 1;
    this.editItems[index].quantity = normalized >= 1 ? normalized : 1;
    this.refreshEditTotalsFromItems();
  }

  openEditTaxPopup(index: number): void {
    if (index < 0 || index >= this.editItems.length) {
      return;
    }

    const item = this.editItems[index];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const currentTaxAmount = Number.isFinite(item.taxAmount ?? 0) ? (item.taxAmount ?? 0) : 0;

    this.editTaxPopupItemIndex = index;
    this.editTaxPopupValue = lineTotal > 0
      ? this.roundMoney((currentTaxAmount / lineTotal) * 100)
      : 0;
    this.editTaxPopupMode = 'percent';
    this.isEditTaxPopupOpen = true;
  }

  closeEditTaxPopup(): void {
    this.isEditTaxPopupOpen = false;
    this.editTaxPopupItemIndex = null;
    this.editTaxPopupValue = 0;
    this.editTaxPopupMode = 'percent';
  }

  applyEditTaxPopup(): void {
    if (this.editTaxPopupItemIndex === null || this.editTaxPopupItemIndex < 0 || this.editTaxPopupItemIndex >= this.editItems.length) {
      this.closeEditTaxPopup();
      return;
    }

    const item = this.editItems[this.editTaxPopupItemIndex];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const amountOrPercent = this.normalizeLineItemAmount(this.editTaxPopupValue);
    item.taxAmount = this.roundMoney(this.editTaxPopupMode === 'percent'
      ? lineTotal * (amountOrPercent / 100)
      : amountOrPercent);

    this.closeEditTaxPopup();
    this.refreshEditTotalsFromItems();
  }

  openEditDiscountPopup(index: number): void {
    if (index < 0 || index >= this.editItems.length) {
      return;
    }

    this.editDiscountPopupItemIndex = index;
    this.editDiscountPopupValue = Number.isFinite(this.editItems[index].discountAmount) ? this.editItems[index].discountAmount : 0;
    this.editDiscountPopupMode = 'amount';
    this.isEditDiscountPopupOpen = true;
  }

  closeEditDiscountPopup(): void {
    this.isEditDiscountPopupOpen = false;
    this.editDiscountPopupItemIndex = null;
    this.editDiscountPopupValue = 0;
    this.editDiscountPopupMode = 'amount';
  }

  applyEditDiscountPopup(): void {
    if (this.editDiscountPopupItemIndex === null || this.editDiscountPopupItemIndex < 0 || this.editDiscountPopupItemIndex >= this.editItems.length) {
      this.closeEditDiscountPopup();
      return;
    }

    const item = this.editItems[this.editDiscountPopupItemIndex];
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
    const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
    const lineTotal = quantity * soldPrice;
    const amountOrPercent = this.normalizeLineItemAmount(this.editDiscountPopupValue);

    item.discountAmount = this.roundMoney(this.editDiscountPopupMode === 'percent'
      ? lineTotal * (amountOrPercent / 100)
      : amountOrPercent);

    this.closeEditDiscountPopup();
    this.refreshEditTotalsFromItems();
  }

  isTipAmountInvalid(value: number | null | undefined): boolean {
    return value !== null && value !== undefined && value <= 0;
  }

  normalizeTipAmount(value: number | null | undefined): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : undefined;
  }

  toggleExpanded(invoiceId: number): void {
    if (this.expandedInvoiceId === invoiceId) {
      this.expandedInvoiceId = null;
      return;
    }

    const invoice = this.invoices.find(i => i.id === invoiceId);
    if (!invoice) {
      this.expandedInvoiceId = null;
      return;
    }

    const organization = this.organizations.find(o => o.id === invoice.organizationId);

    this.expandedInvoiceId = invoiceId;
    this.editForm = {
      invoiceNumber: invoice.invoiceNumber,
      helcimInvoiceId: invoice.helcimInvoiceId ?? '',
      currency: invoice.currency,
      type: invoice.type,
      status: invoice.status ?? 'DUE',
      pickupDate: invoice.pickupDate,
      dateIssued: invoice.dateIssued,
      dateUpdated: invoice.dateUpdated,
      datePaid: invoice.datePaid ?? '',
      amount: invoice.amount,
      amountPaid: invoice.amountPaid,
      amountDue: invoice.amountDue,
      shippingAmount: invoice.shippingAmount,
      shippingDetails: invoice.shippingDetails ?? '',
      totalTaxAmount: invoice.totalTaxAmount,
      totalTaxDetails: invoice.totalTaxDetails ?? '',
      totalDiscountAmount: invoice.totalDiscountAmount,
      totalDiscountDetails: invoice.totalDiscountDetails ?? '',
      tipAmount: invoice.tipAmount ?? null,
      pickupName: invoice.pickupName,
      notes: invoice.notes ?? '',
      contactName: organization?.contactName ?? '',
      legalName: organization?.legalName ?? '',
      billingBusinessName: organization?.billingBusinessName ?? '',
      shippingBusinessName: organization?.shippingBusinessName ?? '',
      billingContactName: organization?.billingContactName ?? '',
      billingContactEmail: organization?.billingContactEmail ?? '',
      billingContactPhone: organization?.billingContactPhone ?? '',
      billingAddress1: organization?.billingAddress1 ?? '',
      billingAddress2: organization?.billingAddress2 ?? '',
      billingCity: organization?.billingCity ?? '',
      billingState: organization?.billingState ?? '',
      billingCountry: organization?.billingCountry ?? '',
      billingZipCode: organization?.billingZipCode ?? '',
      shippingContactName: organization?.shippingContactName ?? '',
      shippingContactEmail: organization?.shippingContactEmail ?? '',
      shippingContactPhone: organization?.shippingContactPhone ?? '',
      shippingAddress1: organization?.shippingAddress1 ?? '',
      shippingAddress2: organization?.shippingAddress2 ?? '',
      shippingCity: organization?.shippingCity ?? '',
      shippingState: organization?.shippingState ?? '',
      shippingCountry: organization?.shippingCountry ?? '',
      shippingZipCode: organization?.shippingZipCode ?? ''
    };

    this.editItems = invoice.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        soldPrice: item.soldPrice,
        soldDescription: item.soldDescription,
        taxAmount: item.taxAmount ?? 0,
        discountAmount: item.discountAmount ?? 0
      }));

    this.editProductAmounts = {};
    this.closeEditTaxPopup();
    this.closeEditDiscountPopup();
    this.refreshEditTotalsFromItems();
  }

  async saveInvoice(invoiceId: number): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    const editTipAmount = this.normalizeTipAmount(this.editForm.tipAmount);

    if (this.isTipAmountInvalid(editTipAmount)) {
      this.errorMessage = 'Tip must be greater than 0 when provided.';
      return;
    }

    this.isSaving = true;

    try {
      this.editForm.dateUpdated = this.getCurrentUtcIsoTimestamp();

      const request: InvoiceUpdateRequest = {
        helcimInvoiceId: this.editForm.helcimInvoiceId.trim() || undefined,
        invoiceNumber: this.editForm.invoiceNumber.trim(),
        currency: this.editForm.currency.trim().toUpperCase(),
        type: this.editForm.type.trim(),
        status: this.editForm.status.trim(),
        dateIssued: this.editForm.dateIssued.trim() || undefined,
        amount: this.editForm.amount,
        amountPaid: this.editForm.amountPaid,
        amountDue: this.editForm.amountDue,
        shippingAmount: this.editForm.shippingAmount,
        shippingDetails: this.editForm.shippingDetails.trim() || undefined,
        totalTaxAmount: this.editForm.totalTaxAmount,
        totalTaxDetails: this.editForm.totalTaxDetails.trim() || undefined,
        totalDiscountAmount: this.editForm.totalDiscountAmount,
        totalDiscountDetails: this.editForm.totalDiscountDetails.trim() || undefined,
        tipAmount: editTipAmount,
        pickupDate: this.editForm.pickupDate.trim() || undefined,
        pickupName: this.editForm.pickupName.trim(),
        notes: this.editForm.notes.trim() || undefined,
        contactName: this.editForm.contactName.trim() || undefined,
        legalName: this.editForm.legalName.trim() || undefined,
        billingBusinessName: this.editForm.billingBusinessName.trim() || undefined,
        shippingBusinessName: this.editForm.shippingBusinessName.trim() || undefined,
        billingContactName: this.editForm.billingContactName.trim() || undefined,
        billingContactEmail: this.editForm.billingContactEmail.trim() || undefined,
        billingContactPhone: this.editForm.billingContactPhone.trim() || undefined,
        billingAddress1: this.editForm.billingAddress1.trim() || undefined,
        billingAddress2: this.editForm.billingAddress2.trim() || undefined,
        billingCity: this.editForm.billingCity.trim() || undefined,
        billingState: this.editForm.billingState.trim() || undefined,
        billingCountry: this.editForm.billingCountry.trim() || undefined,
        billingZipCode: this.editForm.billingZipCode.trim() || undefined,
        shippingContactName: this.editForm.shippingContactName.trim() || undefined,
        shippingContactEmail: this.editForm.shippingContactEmail.trim() || undefined,
        shippingContactPhone: this.editForm.shippingContactPhone.trim() || undefined,
        shippingAddress1: this.editForm.shippingAddress1.trim() || undefined,
        shippingAddress2: this.editForm.shippingAddress2.trim() || undefined,
        shippingCity: this.editForm.shippingCity.trim() || undefined,
        shippingState: this.editForm.shippingState.trim() || undefined,
        shippingCountry: this.editForm.shippingCountry.trim() || undefined,
        shippingZipCode: this.editForm.shippingZipCode.trim() || undefined,
        items: this.editItems
      };

      await firstValueFrom(this.invoiceService.update(invoiceId, request));
      this.successMessage = 'Invoice updated successfully.';
      this.isSaving = false;
      this.changeDetectorRef.detectChanges();
      await this.loadInvoices();
      this.expandedInvoiceId = null;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Update invoice failed.';
    } finally {
      if (this.isSaving) {
        this.isSaving = false;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  refreshEditTotalsFromItems(): void {
    const lineSubtotal = this.editItems.reduce((total, item) => {
      const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
      const soldPrice = Number.isFinite(item.soldPrice) ? item.soldPrice : 0;
      return total + (quantity * soldPrice);
    }, 0);

    const lineTaxTotal = this.editItems.reduce((total, item) => {
      const taxAmount = Number.isFinite(item.taxAmount ?? 0) ? (item.taxAmount ?? 0) : 0;
      return total + taxAmount;
    }, 0);

    const lineDiscountTotal = this.editItems.reduce((total, item) => {
      const discountAmount = Number.isFinite(item.discountAmount) ? item.discountAmount : 0;
      return total + discountAmount;
    }, 0);

    const shippingAmount = Number.isFinite(this.editForm.shippingAmount) ? this.editForm.shippingAmount : 0;

    this.editForm.totalTaxAmount = this.roundMoney(lineTaxTotal);
    this.editForm.totalDiscountAmount = this.roundMoney(lineDiscountTotal);
    this.editForm.amount = this.roundMoney(lineSubtotal + shippingAmount - lineDiscountTotal + lineTaxTotal);
    this.editForm.amountDue = this.editForm.amount;
  }

  private roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private createDefaultInvoiceNumber(): string {
    const now = new Date();
    const year = String(now.getFullYear());
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hour}${minute}`;
  }

  private getCurrentUtcIsoTimestamp(): string {
    return new Date().toISOString();
  }

  private normalizeLineItemAmount(value: number | string): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return parsed < 0 ? 0 : parsed;
  }

  private getOrCreateAmounts(
    target: Record<number, { taxAmount: number; discountAmount: number }>,
    productId: number): { taxAmount: number; discountAmount: number } {
    const existing = target[productId];
    if (existing) {
      return existing;
    }

    target[productId] = { taxAmount: 0, discountAmount: 0 };
    return target[productId];
  }

  private resetCreateForm(): void {
    this.createForm = {
      invoiceNumber: this.createDefaultInvoiceNumber(),
      helcimInvoiceId: '',
      currency: 'USD',
      pickupDate: '',
      type: 'INVOICE',
      status: 'DUE',
      pickupName: '',
      datePaid: '',
      totalTaxAmount: 0,
      totalTaxDetails: '',
      totalDiscountAmount: 0,
      totalDiscountDetails: '',
      dateIssued: this.getCurrentUtcIsoTimestamp(),
      amount: 0,
      amountPaid: 0,
      amountDue: 0,
      shippingAmount: 0,
      shippingDetails: '',
      tipAmount: null,
      depositAmount: 0,
      convenienceFee: 0,
      convenienceFeeEnabled: true,
      notes: ''
    };

    this.createItems = [];
    this.createProductAmounts = {};
    this.createDiscountMode = 'amount';
    this.closeCreateTaxPopup();
    this.closeCreateDiscountPopup();
  }
}
